import {
    ChangeDetectionStrategy,
    Component,
    computed,
    HostBinding,
    Inject,
    inject,
    input,
    Input,
    output,
    signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { fromAuth } from '@pcp/auth';
import { IDisplaySlide, ImageQuality } from '@pcp/core/interfaces';
import { swapImageQualityInUrl } from '@pcp/streaming';
import { PCP_SHARED_PREFERRED_IMAGE_QUALITY } from 'shared/util-runtime-config';
import { combineLatest, Observable, of } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { ResizedDirective } from 'shared/ui/resize-observer';
import { ResizedEvent } from '@pcp/core/util';
import { ActiveElementUiStateService } from 'shared/classroom/data-access';
import { SlidePreviewFacade } from './slide-preview.facade';
import { ClassroomEmitGateService } from './classroom-emit-gate.service';
// eslint-disable-next-line @nx/enforce-module-boundaries -- known umbrella-level cycle, inversion planned: the preview screen must mount extension apps via a registry token instead of importing the integration lib (docs/architecture-review-classroom.md F6/R6)
import { ExtensionAppService } from 'extension-apps/integration';
import { ClassroomEvents, UserClickedOnAddToFavoritesButtonEvent } from 'shared/classroom/data-access';
import * as InternalPreviewScreenActions from '../internal/internal-preview-screen.actions';
import { fromToolbar } from 'shared/classroom/toolbar/data-access';
import { ClassroomConfig, PCP_CLASSROOM_SYNC_CONFIG } from 'shared/classroom/data-access';
import { fromClassroom } from 'shared/classroom/data-access';
import { SLIDE_SYNC_NAVIGATION_PREVIEW_ENABLED } from '../injection-tokens';
import { AsyncPipe } from '@angular/common';
import { ToolbarComponent } from 'shared/classroom/toolbar/feature-shell';
import { FullscreenButtonComponent } from '../components/fullscreen-button.component';
import { SlidePreviewElementComponent } from './slide-preview-element/slide-preview-element.component';
import { PauseImageComponent } from 'shared/classroom/preview-screen/slide-rendering/ui';
import { NextSlidesPreviewComponent } from './next-slides-preview/next-slides-preview.component';
import { NextSlidesPreviewGlassmorphicComponent } from './next-slides-preview/next-slides-preview-glassmorphic.component';

const VIEWPORT_WIDTH = 1280;
const MAX_SCALE = 1.9;

/** Display-resolution qualities that map to a real image variant (mirrors `fabric-slide-canvas`). */
const DISPLAY_QUALITIES: ReadonlySet<string> = new Set([ImageQuality.LOW, ImageQuality.MEDIUM, ImageQuality.HIGH]);

@Component({
    selector: 'app-slide-preview',
    templateUrl: './slide-preview.component.html',
    styleUrls: ['./slide-preview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [SlidePreviewFacade, ClassroomEmitGateService],
    imports: [
        ToolbarComponent,
        FullscreenButtonComponent,
        SlidePreviewElementComponent,
        PauseImageComponent,
        NextSlidesPreviewComponent,
        NextSlidesPreviewGlassmorphicComponent,
        AsyncPipe,
        ResizedDirective,
    ],
})
export class SlidePreviewComponent {
    /**
     * Toolbar and preview image next to each other (horizontal) or below each other (vertical).
     * horizontal is used for PCP, vertical for Wendel.
     */
    @Input() direction: 'horizontal' | 'vertical' = 'vertical';
    // @Input() direction: 'horizontal'|'vertical' = 'horizontal';
    @HostBinding('class.horizontal') get isHorizontal() {
        return this.direction === 'horizontal';
    }
    @HostBinding('class.vertical') get isVertical() {
        return this.direction === 'vertical';
    }
    /** Floating drawing toolbar instead of the fixed one. Needs `glassTheme` (only the glassmorphic nav has it). */
    @Input() floatingToolbar: boolean = false;
    /** Beamer "frozen glass" nav (`pcp-next-slides-preview-glassmorphic`). false = classic nav (TUT beamer). */
    readonly glassTheme = input(false);

    /** If true, shows an additional button to toggle fullscreen mode for the slide preview image + the slide sync toolbar. */
    showToggleFullscreen = input<boolean>(false);
    readonly isPreviewInFullscreen = input<boolean>(false);
    fullscreenButtonClicked = output();

    readonly isPresentationScreen = this.config.isPresentationScreen;
    readonly emitHtmlElementModifications = this.config.emitHtmlElementModifications;
    private readonly showToolbar$ = of(this.config.showToolbar).pipe(
        switchMap(showToolbar =>
            this.isPresentationScreen
                ? this.navigationPreviewEnabled.pipe(map(enabled => enabled && showToolbar))
                : of(showToolbar === true),
        ),
    );
    readonly showFixedToolbar$ = this.showToolbar$.pipe(map(toolbar => toolbar && !this.floatingToolbar));
    readonly showFloatingToolbar$ = this.showToolbar$.pipe(map(toolbar => toolbar && this.floatingToolbar));
    readonly useFixedToolbar = this.config.useFixedToolbar === true;
    readonly disableElementUsage = this.config.disableElementUsage === true;

    readonly layers = this.facade.layers;

    readonly enetToken$ = this.store.select(fromAuth.selectEnetToken);
    readonly loginCode$ = this.store.select(fromClassroom.selectStudentRegistrationCode);
    readonly presentationId$ = this.store.select(fromClassroom.selectPresentationId);
    readonly isPresentationStarted$ = this.store.select(fromClassroom.selectIsPresentationStarted);

    readonly drawingTool$ = this.store.select(fromToolbar.selectActivatedDrawingTool);

    private readonly preferredQuality$ = inject(PCP_SHARED_PREFERRED_IMAGE_QUALITY);
    /**
     * The pause/fallback image URL, swapped to this client's preferred quality variant
     * (teacher pinned low, beamer follows the saved setting). Non-display / unknown quality
     * → the URL is used as-is. Null when no pause image is configured.
     */
    readonly pauseImageUrl$ = combineLatest([
        this.store.select(fromClassroom.selectPauseImageUrl),
        this.preferredQuality$.pipe(startWith(undefined)),
    ]).pipe(
        map(([url, quality]) =>
            url && quality && DISPLAY_QUALITIES.has(quality)
                ? swapImageQualityInUrl(url, quality as ImageQuality)
                : (url ?? null),
        ),
    );
    readonly isLessonPaused$ = this.store.select(fromClassroom.isLessonPaused);
    private readonly uiState = inject(ActiveElementUiStateService);
    readonly showFallbackImage$ = toObservable(this.uiState.shouldShowFallbackImage);
    readonly isShowingIntegratedApp$ = this.extensionAppService.showIntegratedApp$;

    private readonly isFloatingToolbarOpenedInStore = this.store.selectSignal(fromToolbar.selectIsFloatingToolbarOpened);
    // requireSync is safe: ExtensionAppService.integratedApp$ starts with startWith(null)
    private readonly isShowingIntegratedApp = toSignal(this.isShowingIntegratedApp$, { requireSync: true });
    /** The floating toolbar is visible: opened in the store, and no integrated app covers it. */
    readonly isFloatingToolbarOpened = computed(
        () => this.isFloatingToolbarOpenedInStore() && !this.isShowingIntegratedApp()
    );

    constructor(
        private readonly facade: SlidePreviewFacade,
        private readonly store: Store,
        private readonly extensionAppService: ExtensionAppService,
        @Inject(PCP_CLASSROOM_SYNC_CONFIG)
        private readonly config: ClassroomConfig,
        @Inject(SLIDE_SYNC_NAVIGATION_PREVIEW_ENABLED)
        readonly navigationPreviewEnabled: Observable<boolean>,
    ) {}

    onPrevBookpageClicked(_currentSlide: IDisplaySlide) {
        this.store.dispatch(InternalPreviewScreenActions.showPreviousBookpageLinkHasBeenClicked());
    }

    onNextBookpageClicked(_currentSlide: IDisplaySlide) {
        this.store.dispatch(InternalPreviewScreenActions.showNextBookpageLinkHasBeenClicked());
    }

    protected readonly svgScale = signal<number | null>(null);

    protected onContentResized(event: ResizedEvent): void {
        if (event.newWidth > 0) {
            // scale a bit bigger than calculated, but stop at a certain point
            // to make sure the buttons do not become too big.
            const ratio = event.newWidth / VIEWPORT_WIDTH;
            this.svgScale.set(Math.min(ratio + 0.5, MAX_SCALE));
        }
    }

    /**
     * Received event from either the primary or the secondary slide element.
     * Dispatch an event to the store which can be handled from the teacher (or beamer) project.
     */
    async addToFavorites(favoriteToSave: UserClickedOnAddToFavoritesButtonEvent) {
        this.store.dispatch(ClassroomEvents.addedToFavorites(favoriteToSave));
    }
}
